import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CommunicationPanel = () => {
  const [activeChannel, setActiveChannel] = useState('blueprint');
  const [messageText, setMessageText] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  // Mock communication channels
  const communicationChannels = [
    {
      id: 'blueprint',
      name: 'Blueprint Protocol',
      description: 'Strategic communications alignment',
      icon: 'Shield',
      status: 'active',
      participants: 12,
      lastActivity: new Date(Date.now() - 300000)
    },
    {
      id: 'crisis-coord',
      name: 'Crisis Coordination',
      description: 'Multi-institutional coordination',
      icon: 'Users',
      status: 'active',
      participants: 8,
      lastActivity: new Date(Date.now() - 600000)
    },
    {
      id: 'public-comms',
      name: 'Public Communications',
      description: 'Coherent public messaging',
      icon: 'Megaphone',
      status: 'monitoring',
      participants: 15,
      lastActivity: new Date(Date.now() - 900000)
    },
    {
      id: 'platform-liaison',
      name: 'Platform Liaison',
      description: 'VLOP coordination channel',
      icon: 'Link',
      status: 'standby',
      participants: 6,
      lastActivity: new Date(Date.now() - 1800000)
    }
  ];

  // Mock recent messages
  const [messages] = useState([
    {
      id: 1,
      channel: 'blueprint',
      sender: 'MoFA Coordinator',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face',
      content: `Coordinated response activated for FIMI-2024-0921-001. All agencies align messaging with approved narrative framework.\n\nKey points:\n- Emphasize institutional resilience\n- Highlight cross-border cooperation\n- Maintain factual accuracy`,
      timestamp: new Date(Date.now() - 300000),
      priority: 'high',
      attachments: [
        { type: 'document', name: 'Response_Framework_v2.pdf', size: '1.2 MB' }
      ]
    },
    {
      id: 2,
      channel: 'crisis-coord',
      sender: 'EFCSN Lead',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=32&h=32&fit=crop&crop=face',
      content: 'Fact-checking teams deployed across affected regions. Initial verification complete for 15 claims. Counter-narrative materials ready for distribution.',
      timestamp: new Date(Date.now() - 600000),
      priority: 'medium'
    },
    {
      id: 3,
      channel: 'public-comms',
      sender: 'Communications Director',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face',
      content: 'Public statement draft ready for review. Coordinating with national press offices for synchronized release at 14:00 CET.',
      timestamp: new Date(Date.now() - 900000),
      priority: 'medium'
    },
    {
      id: 4,
      channel: 'blueprint',
      sender: 'MoI Analyst',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=32&h=32&fit=crop&crop=face',
      content: 'Platform response tracking updated. Facebook: Content flagged, Twitter: Trending suppressed, TikTok: Awaiting response.',
      timestamp: new Date(Date.now() - 1200000),
      priority: 'low'
    }
  ]);

  const recipients = [
    { id: 'mofa-coord', name: 'MoFA Coordinator', role: 'Primary', status: 'online' },
    { id: 'moi-analyst', name: 'MoI Analyst', role: 'Primary', status: 'online' },
    { id: 'mod-liaison', name: 'MoD Liaison', role: 'Primary', status: 'away' },
    { id: 'efcsn-lead', name: 'EFCSN Lead', role: 'Expert', status: 'online' },
    { id: 'edmo-coord', name: 'EDMO Coordinator', role: 'Expert', status: 'offline' },
    { id: 'comms-dir', name: 'Communications Director', role: 'Support', status: 'online' }
  ];

  const getChannelStatus = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'monitoring': return 'text-yellow-600 bg-yellow-50';
      case 'standby': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-red-500';
      case 'medium': return 'border-l-yellow-500';
      case 'low': return 'border-l-green-500';
      default: return 'border-l-gray-300';
    }
  };

  const formatTimeAgo = (date) => {
    const minutes = Math.floor((Date.now() - date?.getTime()) / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleSendMessage = () => {
    if (!messageText?.trim()) return;
    
    // Mock message sending
    alert(`Message sent to ${activeChannel} channel: "${messageText}"`);
    setMessageText('');
  };

  const activeChannelData = communicationChannels?.find(c => c?.id === activeChannel);
  const channelMessages = messages?.filter(m => m?.channel === activeChannel);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Icon name="MessageSquare" size={20} className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Strategic Communications</h2>
            <p className="text-sm text-gray-500">Blueprint-aligned coordination</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Settings"
            iconPosition="left"
          >
            Settings
          </Button>
        </div>
      </div>
      <div className="flex flex-1 min-h-0">
        {/* Channel Sidebar */}
        <div className="w-80 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Communication Channels</h3>
            <div className="space-y-2">
              {communicationChannels?.map((channel) => (
                <button
                  key={channel?.id}
                  onClick={() => setActiveChannel(channel?.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    activeChannel === channel?.id
                      ? 'border-blue-300 bg-blue-50' :'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon name={channel?.icon} size={16} className="text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">{channel?.name}</span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getChannelStatus(channel?.status)}`}>
                      {channel?.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">{channel?.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{channel?.participants} participants</span>
                    <span>{formatTimeAgo(channel?.lastActivity)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Online Users */}
          <div className="p-4 flex-1">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Active Participants</h4>
            <div className="space-y-2">
              {recipients?.filter(r => r?.status === 'online')?.map((recipient) => (
                <div key={recipient?.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                  <div className="relative">
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                      <Icon name="User" size={14} className="text-gray-600" />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{recipient?.name}</p>
                    <p className="text-xs text-gray-500">{recipient?.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Message Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Channel Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name={activeChannelData?.icon} size={20} className="text-gray-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{activeChannelData?.name}</h3>
                  <p className="text-sm text-gray-500">{activeChannelData?.description}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getChannelStatus(activeChannelData?.status)}`}>
                  {activeChannelData?.status}
                </span>
                <span className="text-sm text-gray-500">{activeChannelData?.participants} participants</span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {channelMessages?.map((message) => (
              <div key={message?.id} className={`border-l-4 pl-4 py-3 ${getPriorityColor(message?.priority)}`}>
                <div className="flex items-start space-x-3">
                  <img
                    src={message?.avatar}
                    alt={message?.sender}
                    className="w-8 h-8 rounded-full"
                    onError={(e) => {
                      e.target.src = '/assets/images/no_image.png';
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-gray-900">{message?.sender}</span>
                      <span className="text-xs text-gray-500">{formatTimeAgo(message?.timestamp)}</span>
                      {message?.priority === 'high' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                          HIGH
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">{message?.content}</div>
                    
                    {message?.attachments && (
                      <div className="mt-2 space-y-1">
                        {message?.attachments?.map((attachment, index) => (
                          <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded border">
                            <Icon name="FileText" size={16} className="text-gray-500" />
                            <span className="text-sm text-gray-700">{attachment?.name}</span>
                            <span className="text-xs text-gray-500">({attachment?.size})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Message Input */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder={`Send message to ${activeChannelData?.name}...`}
                  value={messageText}
                  onChange={(e) => setMessageText(e?.target?.value)}
                  onKeyPress={(e) => {
                    if (e?.key === 'Enter' && !e?.shiftKey) {
                      e?.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  iconName="Paperclip"
                  onClick={() => {
                    // File attachment functionality
                  }}
                />
                
                <Button
                  variant="default"
                  iconName="Send"
                  onClick={handleSendMessage}
                  disabled={!messageText?.trim()}
                >
                  Send
                </Button>
              </div>
            </div>
            
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
              <span>Press Enter to send, Shift+Enter for new line</span>
              <span>Blueprint Protocol Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationPanel;